import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router";
import ReviewList from "../modules/review/components/review-list";
import ReviewMasonry from "../modules/review/components/review-masonry";
import ReviewFiltersComponent from "../modules/review/components/review-filters";
import { useReviews } from "../modules/review/hooks/use-reviews";
import { useTags } from "../modules/tag/hooks/use-tags";
import { useAuth } from "../modules/auth/hooks/use-auth";
import type { Review, ReviewFilters } from "../modules/review/types";
import { ReviewListSkeleton } from "@/modules/review";
import Button from "@/components/ui/button";
import Container from "@/components/ui/container";
import { Tooltip } from "@/components/ui/tooltip";
import { useOnboardingStore } from "@/modules/onboarding/stores/onboarding-store";
import confetti from "canvas-confetti";
import { useBrands, usePlan } from "@/modules/brand";
import { hasReachedReviewLimit } from "@/modules/brand/utils/plan-restrictions";
import UpgradeToGrowthModal from "@/components/ui/upgrade-to-growth-modal";
import IconInfo from "@/components/icons/icon-info";
import { getMockReviews } from "@/modules/review/constants/mock-reviews";

type ViewMode = "list" | "masonry";

function ViewToggle({ value, onChange }: { value: ViewMode; onChange: (mode: ViewMode) => void }) {
	return (
		<div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
			<button
				type="button"
				onClick={() => onChange("masonry")}
				className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${value === "masonry" ? "bg-white text-primary shadow-sm" : "text-tertiary hover:text-primary"}`}
			>
				<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"
					/>
				</svg>
			</button>
			<button
				type="button"
				onClick={() => onChange("list")}
				className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${value === "list" ? "bg-white text-primary shadow-sm" : "text-tertiary hover:text-primary"}`}
			>
				<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
				</svg>
			</button>
		</div>
	);
}

export default function ReviewsPage() {
	const navigate = useNavigate();
	const { brandUrl } = useParams();
	const { currentWorkspaceId } = useAuth();
	const { indexReviews, deleteReview, approveReview, unapproveReview, getStatusCounts } = useReviews();
	const { tags, indexTags } = useTags();
	const { selectedBrand, viewBrand } = useBrands();
	const { planDetails, getPlan } = usePlan();

	const [reviews, setReviews] = useState<Review[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [viewMode, setViewMode] = useState<ViewMode>("masonry");
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [filters, setFilters] = useState<ReviewFilters>({});
	const filtersRef = useRef<ReviewFilters>({});
	const [statusChangingReviewId, setStatusChangingReviewId] = useState<string | null>(null);
	const [pendingCount, setPendingCount] = useState<number>(0);
	const [totalCount, setTotalCount] = useState<number>(0);
	const [draftCount, setDraftCount] = useState<number | null>(0);
	const [hiddenCount, setHiddenCount] = useState<number>(0);
	const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
	const [showMockReviews, setShowMockReviews] = useState(false);
	const mockReviews = useMemo(() => getMockReviews(selectedBrand?.name), [selectedBrand?.name]);

	// Load tags for filter
	useEffect(() => {
		if (currentWorkspaceId) {
			indexTags(currentWorkspaceId);
		}
	}, [currentWorkspaceId, indexTags]);

	// Load status counts
	const loadStatusCounts = useCallback(
		async (currentFilters?: ReviewFilters) => {
			if (!currentWorkspaceId) return;
			const activeFilters = currentFilters ?? filtersRef.current;
			const counts = await getStatusCounts(currentWorkspaceId, activeFilters);
			if (counts) {
				setPendingCount(counts.pending);
				setDraftCount(counts?.draft || null);
				setTotalCount(counts.total);
				setHiddenCount(counts.hidden_count);
			}
		},
		[currentWorkspaceId, getStatusCounts],
	);

	useEffect(() => {
		loadStatusCounts();
	}, [loadStatusCounts]);

	// Load brand data to get user role and plan data for limits
	useEffect(() => {
		if (currentWorkspaceId) {
			viewBrand(currentWorkspaceId);
			getPlan(currentWorkspaceId);
		}
	}, [currentWorkspaceId, viewBrand, getPlan]);

	const loadReviews = useCallback(
		async (cursor?: string | null, currentFilters?: ReviewFilters) => {
			if (!currentWorkspaceId) return;

			if (cursor) {
				setIsLoadingMore(true);
			} else {
				setIsLoading(true);
			}
			setError(null);

			try {
				const activeFilters = currentFilters ?? filtersRef.current;
				const response = await indexReviews(currentWorkspaceId, cursor, activeFilters);
				if (response) {
					if (cursor) {
						// Append to existing reviews
						setReviews((prev) => [...prev, ...response.data]);
					} else {
						setReviews(response.data);
					}
					setNextCursor(response.meta.next_cursor);
					if (response.data.length > 0 && currentWorkspaceId) {
						const store = useOnboardingStore.getState();
						if (!store.completedSteps.includes("collect_testimonial")) {
							store.completeStep(currentWorkspaceId, "collect_testimonial");
							confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
							import("@/lib/analytics").then(({ trackEvent }) => {
								trackEvent("activation", { method: "first_testimonial" }, "Activated");
							});
						}
					}
				} else {
					setError("Failed to load reviews");
				}
			} catch (err) {
				setError(err instanceof Error ? err.message : "An error occurred while loading reviews");
			} finally {
				setIsLoading(false);
				setIsLoadingMore(false);
			}
		},
		[indexReviews, currentWorkspaceId],
	);

	useEffect(() => {
		loadReviews();
	}, [loadReviews]);

	const handleViewDrafts = () => {
		navigate(`/${brandUrl}/reviews/drafts`);
	};

	const handleFiltersChange = (newFilters: ReviewFilters) => {
		setFilters(newFilters);
		filtersRef.current = newFilters;
		setNextCursor(null);
		loadReviews(null, newFilters);
		loadStatusCounts(newFilters);
	};

	const handleLoadMore = () => {
		if (nextCursor && !isLoadingMore) {
			loadReviews(nextCursor);
		}
	};

	const handleDeleteReview = async (id: string) => {
		if (!currentWorkspaceId) {
			throw new Error("No workspace selected");
		}

		const result = await deleteReview(currentWorkspaceId, id);
		if (!result) {
			throw new Error("Failed to delete review");
		}

		// Remove the deleted review from the list
		setReviews(reviews.filter((review) => review.id !== id));
		// Refresh status counts
		loadStatusCounts();
		// Refetch plan data to update restrictions
		if (currentWorkspaceId) {
			getPlan(currentWorkspaceId);
		}
	};

	const handleAddReview = () => {
		if (planDetails && hasReachedReviewLimit(planDetails.usage, planDetails.limits, "total")) {
			setIsUpgradeModalOpen(true);
			return;
		}
		if (currentWorkspaceId) {
			getPlan(currentWorkspaceId);
		}
		navigate(`/${brandUrl}/reviews/create`);
	};

	const handleEditReview = (id: string) => {
		navigate(`/${brandUrl}/reviews/${id}/edit`);
	};

	const handleApprove = async (id: string) => {
		if (!currentWorkspaceId) return;

		setStatusChangingReviewId(id);
		try {
			const result = await approveReview(currentWorkspaceId, id);
			if (result) {
				// Update the review status in the local state using the API response
				setReviews((prev) => prev.map((review) => (review.id === id ? { ...review, status: result.status } : review)));
				// Refresh status counts
				loadStatusCounts();
			}
		} finally {
			setStatusChangingReviewId(null);
		}
	};

	const handleUnapprove = async (id: string) => {
		if (!currentWorkspaceId) return;

		setStatusChangingReviewId(id);
		try {
			const result = await unapproveReview(currentWorkspaceId, id);
			if (result) {
				// Update the review status in the local state using the API response
				setReviews((prev) => prev.map((review) => (review.id === id ? { ...review, status: result.status } : review)));
				// Refresh status counts
				loadStatusCounts();
			}
		} finally {
			setStatusChangingReviewId(null);
		}
	};

	const handleViewReview = (id: string) => {
		navigate(`/${brandUrl}/reviews/${id}`);
	};

	const hasNoReviews = !isLoading && !error && reviews.length === 0;
	const hasActiveFilters = Object.keys(filters).length > 0;
	const reviewsToDisplay = showMockReviews && hasNoReviews ? mockReviews : reviews;

	useEffect(() => {
		if (!hasNoReviews) {
			setShowMockReviews(false);
		}
	}, [hasNoReviews]);

	// Empty state when filters applied but no results
	const renderFilterEmptyState = () => (
		<div className="flex flex-col items-center justify-center py-16 px-4">
			<div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
				<svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
				</svg>
			</div>
			<h3 className="text-base font-semibold text-primary mb-1">No matching reviews</h3>
			<p className="text-sm text-tertiary text-center max-w-sm mb-4">No reviews match your current filters. Try adjusting or clearing your filters.</p>
			<button type="button" onClick={() => handleFiltersChange({})} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-brand hover:text-brand/80 transition-colors">
				<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
				</svg>
				Clear all filters
			</button>
		</div>
	);

	// Empty state - fully centered layout (no reviews at all)
	if (hasNoReviews && !hasActiveFilters && !showMockReviews) {
		return (
			<Container className="flex flex-col items-center justify-center min-h-[60vh] px-4">
				<div className="w-full mb-8">
					<h1 className="text-xl font-semibold text-primary">Reviews</h1>
					<p className="mt-3 text-tertiary max-w-160">Manage all your customer testimonials in one place. Add reviews manually or collect them through feedback forms.</p>
				</div>

				<div className="w-full rounded-xl border border-gray-200 bg-gray-50 p-4 mb-4">
					<div className="flex items-center justify-between gap-2">
						<div className="flex gap-4">
							<div>
								<h2 className="text-base font-semibold text-gray-800 mb-1">No Reviews Yet</h2>
								<p className="text-sm text-gray-700">Curious how your reviews will look? Preview it with sample reviews.</p>
							</div>
						</div>
						<Button type="button" aria-checked={showMockReviews} aria-label="Toggle preview mode" onClick={() => setShowMockReviews((prev) => !prev)} variant="light">
							Show Sample Reviews
						</Button>
					</div>
				</div>

				<div className="flex w-full bg-white rounded-xl border border-gray-200">
					<button
						type="button"
						onClick={handleAddReview}
						className="group flex-1 flex items-center gap-3.5 px-6 py-5 hover:bg-gray-50 transition-colors text-left cursor-pointer border-r border-gray-200"
					>
						<svg className="w-5 h-5 text-muted shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
						</svg>
						<div className="min-w-0">
							<h3 className="font-medium text-primary">Add manually</h3>
							<p className="text-sm text-tertiary">Enter a testimonial you've received</p>
						</div>
					</button>

					<button
						type="button"
						onClick={() => navigate(`/${brandUrl}/forms`)}
						className="group flex-1 flex items-center gap-3.5 px-6 py-5 hover:bg-gray-50 transition-colors text-left cursor-pointer border-r border-gray-200"
					>
						<svg className="w-5 h-5 text-muted shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={1.5}
								d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
							/>
						</svg>
						<div className="min-w-0">
							<h3 className="font-medium text-primary">Share form link</h3>
							<p className="text-sm text-tertiary">Collect feedback from customers</p>
						</div>
					</button>

					<button
						type="button"
						onClick={() => navigate(`/${brandUrl}/import`)}
						className="group flex-1 flex items-center gap-3.5 px-6 py-5 hover:bg-gray-50 transition-colors text-left cursor-pointer"
					>
						<svg className="w-5 h-5 text-muted shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
						</svg>
						<div className="min-w-0">
							<h3 className="font-medium text-primary">Migrate platforms</h3>
							<p className="text-sm text-tertiary">Import from Senja, FameWall & more</p>
						</div>
					</button>
				</div>

				<p className="text-tertiary text-center max-w-md mt-8">Start collecting testimonials from your customers to build trust and grow your brand.</p>
			</Container>
		);
	}

	return (
		<>
			<Container className="py-12">
				<div className="space-y-6">
					<div className="flex items-center justify-between gap-8">
						<div>
							<h1 className="text-xl font-semibold text-primary">Reviews</h1>
							<p className="mt-3 text-tertiary max-w-160">Manage all your customer testimonials in one place. Add reviews manually or collect them through feedback forms.</p>
						</div>
						{!showMockReviews && (
							<div className="flex items-center gap-3">
								<Button onClick={handleAddReview} className="whitespace-nowrap">
									Add Review
								</Button>
							</div>
						)}
					</div>

					{draftCount && draftCount > 0 ? (
						<div className="text-sm text-tertiary flex gap-2 items-center justify-between border border-gray-200 rounded p-3">
							<div className="flex items-center gap-1">
								<IconInfo className="w-4 h-4" />
								<p>
									You have <strong>{draftCount}</strong> incomplete submissions
								</p>
							</div>
							<button type="button" onClick={handleViewDrafts} className="underline font-medium text-tertiar rounded-lg hover:bg-gray-50 hover:text-primary transition-colors">
								View drafts
							</button>
						</div>
					) : null}

					{showMockReviews && (
						<div className="rounded-xl border border-blue-200 bg-blue-50 p-4 sticky top-0 z-20 shadow-sm">
							<div className="flex items-center justify-between gap-4">
								<div className="flex items-start gap-4">
									<div>
										<h3 className="text-base font-semibold text-blue-900 mb-1">Previewing Sample Reviews</h3>
										<p className="text-sm text-blue-800">These are sample reviews and not your actual review data.</p>
									</div>
								</div>
								<Button type="button" onClick={() => setShowMockReviews((prev) => !prev)} variant="light">
									Hide Sample Reviews
								</Button>
							</div>
						</div>
					)}

					{showMockReviews ? (
						<div className="flex items-center gap-2 justify-end">
							<ViewToggle value={viewMode} onChange={setViewMode} />
						</div>
					) : (
						<ReviewFiltersComponent filters={filters} onChange={handleFiltersChange} tags={tags ?? undefined} pendingCount={pendingCount} totalCount={totalCount} isLoading={isLoading}>
							<div className="flex items-center gap-2">
								<ViewToggle value={viewMode} onChange={setViewMode} />
							</div>
						</ReviewFiltersComponent>
					)}

					{hiddenCount > 0 && (
						<div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
							<div className="flex items-center justify-between gap-4">
								<div className="flex items-center gap-3 min-w-0">
									<div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
										<svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
											<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
										</svg>
									</div>
									<div className="min-w-0">
										<h3 className="text-sm font-semibold text-blue-800">
											{hiddenCount} hidden review{hiddenCount > 1 ? "s" : ""}
										</h3>
										<p className="text-xs text-blue-600">
											{selectedBrand?.role === "editor" ? (
												<>Some reviews are hidden due to plan limits. Contact your workspace admin to upgrade.</>
											) : (
												<>Upgrade to Growth to unlock all your reviews and showcase more social proof.</>
											)}
										</p>
									</div>
								</div>
								{selectedBrand?.role !== "editor" && (
									<button
										type="button"
										onClick={() => navigate(`/${currentWorkspaceId}/billing`)}
										className="shrink-0 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
									>
										Upgrade
									</button>
								)}
							</div>
							{selectedBrand?.role !== "editor" && (
								<div className="mt-2 pt-2 border-t border-blue-100">
									<Tooltip
										content={<span>Reviews may be hidden when you've reached your plan limits for text, video, or audio reviews. Upgrade to reveal them all.</span>}
										side="top"
									>
										<button type="button" className="text-xs text-blue-500 hover:text-blue-700 underline cursor-pointer">
											Why are they hidden?
										</button>
									</Tooltip>
								</div>
							)}
						</div>
					)}

					<div>
						{isLoading ? (
							<ReviewListSkeleton />
						) : error ? (
							<div className="bg-red-50 border border-red-200 rounded-md p-4">
								<p className="text-sm text-red-600">{error}</p>
								<button onClick={() => loadReviews()} className="mt-2 text-sm font-medium text-red-600 hover:text-red-700 underline">
									Try again
								</button>
							</div>
						) : reviewsToDisplay.length === 0 && hasActiveFilters ? (
							renderFilterEmptyState()
						) : viewMode === "list" ? (
							<ReviewList
								reviews={reviewsToDisplay}
								onDelete={handleDeleteReview}
								onEdit={handleEditReview}
								onApprove={handleApprove}
								onUnapprove={handleUnapprove}
								onClick={handleViewReview}
								isLoading={false}
								statusChangingReviewId={statusChangingReviewId}
								isDemoMode={showMockReviews}
							/>
						) : (
							<ReviewMasonry
								reviews={reviewsToDisplay}
								onDelete={handleDeleteReview}
								onEdit={handleEditReview}
								onApprove={handleApprove}
								onUnapprove={handleUnapprove}
								onClick={handleViewReview}
								isDemoMode={showMockReviews}
								statusChangingReviewId={statusChangingReviewId}
							/>
						)}

						{/* Load More Button */}
						{!isLoading && !error && nextCursor && (
							<div className="flex justify-center mt-8">
								<button
									type="button"
									onClick={handleLoadMore}
									disabled={isLoadingMore}
									className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-tertiary bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{isLoadingMore ? (
										<>
											<svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
												<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
												<path
													className="opacity-75"
													fill="currentColor"
													d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
												/>
											</svg>
											Loading...
										</>
									) : (
										"Load More"
									)}
								</button>
							</div>
						)}
					</div>
				</div>
			</Container>

			<UpgradeToGrowthModal
				open={isUpgradeModalOpen}
				onOpenChange={setIsUpgradeModalOpen}
				context="reviews_limit"
				workspaceId={currentWorkspaceId ?? undefined}
				reviewLimit={planDetails?.limits.max_total_reviews}
				userRole={selectedBrand?.role}
			/>
		</>
	);
}
